<template>
  <BulkImportOverlay>
    <HeaderBar>
      <router-link
        :aria-label="$t('general.back')"
        class="button button--small"
        :to="{ name: 'inspection', params: { scope: 'inspections' } }"
      >
        <UseSvg
          class="icon"
          name="arrow_left"
        />
      </router-link>
      <div class="header-bar__title typo-title">
        {{ $t('issues.bulk_import') }}
      </div>

      <template #right>
        <FormSubmitButton
          :buttonLabel="$t('issues.import')"
          :isDisabled="formDisabled"
          :isSaving="saving"
          @click="!formDisabled && saveData()"
        />
      </template>
    </HeaderBar>
    <Table
      v-if="currentData?.length > 0"
      :data="currentData"
      identifier="plant"
    />
  </BulkImportOverlay>
</template>
<script setup>
import { onMounted, onUnmounted, ref, reactive, watch } from 'vue';
import { useRouter } from 'vue-router';

// Utils
import I18n from 'i18n-js';
import { useBulkImportStore } from '@/stores/bulk_import/bulkImportStore';
import useAjax from '@/functions/use_ajax';
import { latLngToRgeo } from '@/functions/geospatial_conversions';
import ratingToPriority from '@/functions/simm/rating_to_priority';
import useEmitter from '@/functions/use_emitter';
import useOrganisationProfile from '@/functions/use_organisation_profile';

// Components
import HeaderBar from '@/components/generic/header_bar.vue';
import FormSubmitButton from '@/components/form/form_submit_button.vue';
import Table from '@/components/generic/bulk/table.vue';
import BulkImportOverlay from '@/components/regions/regions_issue_overlay.vue';
import UseSvg from '@/components/generic/use_svg.vue';

const props = defineProps({
  organisationId: { type: String, required: true },
  inspectionId: { type: String, required: true },
  operationId: { type: String, required: true },
});

const ajax = useAjax();
const router = useRouter();
const formDisabled = ref(false);
const currentData = ref([]);
const bulkImportStore = useBulkImportStore();
const saving = ref(false);
const errors = ref(null);
const inspection = reactive({
  id: '',
  date: null,
  operation_id: null,
  inspected_at: null,
  inspection_type: null,
  professional_engineer: null,
  issues: [],
});
const emitter = useEmitter();

const { isAga } = useOrganisationProfile();

watch(
  bulkImportStore,
  (state) => {
    localStorage.setItem('tableData', JSON.stringify(state.tableData));
  },
  { deep: true }
);

onMounted(() => {
  currentData.value = bulkImportStore.tableData;
  fetchInspection();
});

onUnmounted(() => {
  bulkImportStore.reset();
});

function scrollIntoView(event) {
  event.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
}

function updateConditionRating(issue) {
  let allRatings = issue.deterioration_mechanism_ratings_attributes.map(
    (x) => x.value
  );
  return Math.max(0, ...allRatings);
}

function updatePriority(issue) {
  const overallConditionRating = updateConditionRating(issue);
  return ratingToPriority(overallConditionRating, { elementClass: issue.element_class, isAga: isAga.value });
}

function saveData() {
  /**
   * TODO: find better way to validate
   * couldn't use ref and other ways to validate got messy
   */
  const inputErrors = document.querySelectorAll('.input--error');
  const detMechErrors = document.querySelectorAll('.issue-inline-field--error');
  const element = inputErrors.length > 0 ? inputErrors : detMechErrors;
  const isValid = element.length <= 0;
  if (isValid) {
    currentData.value.forEach(issue => {
      issue['location'] = latLngToRgeo([issue.geolocation_latitude, issue.geolocation_longitude]);
      issue['overall_condition_rating'] = updateConditionRating(issue);
      issue['priority'] = updatePriority(issue);
    });
    inspection.issues = currentData.value;
    saveInspection(inspection);
  } else {
    scrollIntoView(element[0]);
  }
}

/**
 * Fetches the inspection details from the server
 */
async function fetchInspection() {
  if (props.inspectionId) {
    const { ok, data } = await ajax(`/organisations/${props.organisationId}/operations/${props.operationId}/plants/inspections/${props.inspectionId}`);
    if (ok) {
      inspection.id = data.id;
      inspection.inspection_type = data.inspection_type;
      inspection.inspected_at = data.inspected_at;
      inspection.date = data.date;
      inspection.professional_engineer = data.professional_engineer;
      inspection.operation_id = data.operation_id;
    }
  }
}

async function saveInspection(inspection) {
  saving.value = true;
  errors.value = null;

  let { ok, data } = await ajax('/uploads/plants_inspection', {
    method: 'POST',
    body: { inspection },
  });

  if (ok) {
    // TODO: Remove when we add images
    process(data.id);
    // TODO: Include when we add images
    // uploadAttachments(data.id, inspection.issues);
  } else {
    errors.value = data;
    saving.value = false;
  }
}

// TODO: Include when we add images
// function imageCollection(data) {
//   // Convert the 'data' into an array and reduce all items to
//   // construct a new array with only images items
//   return Object.values(data).reduce(function (list, item) {
//     // Add the image items to the collection
//     return list.concat(Object.values(item.images));
//   }, []);
// }

// TODO: Include when we add images
// async function uploadAttachments(id, issues) {
//   const images = imageCollection(issues);
//   if (images.length <= 0) return process(id);

//   const promisesToAwait = [];
//   for(let i = 0; i < images.length; i++) {
//     promisesToAwait.push(await loopThroughAllImages(id, images[i]));
//   }
//   await Promise.all(promisesToAwait).then(data => {
//     process(id);
//   });
// }

// async function loopThroughAllImages(id, image) {
//   const response = await ajax(`/uploads/${id}/upload_attachments`, {
//       method: 'POST',
//       body: {
//         file: image.url,
//         type: 'plants_inspection',
//         id: image.id,
//       }
//     });
//   return Promise.resolve(response);
// }

async function process(id) {
  let { ok } = await ajax(`/uploads/${id}/process`, { method: 'POST' });

  if (ok) {
    bulkImportStore.reset();
    emitter.emit('toast', { status: 'warning', message: I18n.t('issues.import_saving') });
    router.push({
      name: 'inspection',
      params: {
        scope: 'inspections',
        organisationId: props.organisationId,
        operationId: props.operationId,
        inspectionId: props.inspectionId
      }
    });
  }
  saving.value = false;
}
</script>

<style scoped lang="scss">
@import '@/stylesheets/global';

.header-bar {
  &__title {
    margin-left: bu(2);
  }
}
</style>
